import { Center, Loader, Stack } from '@mantine/core';
import { GetEvChargingStationResponse } from 'api/actions/get-ev-charging-station/get-ev-charging-station-response';
import { GetEvChargingSessionsResponse } from 'api/actions/get-ev-charging-sessions/get-ev-charging-sessions-response';
import { GetEvChargingSessionStatsResponse } from 'api/actions/get-ev-charging-session-stats/get-ev-charging-session-stats-response';
import { GetEvChargingStationLiveStatusResponse } from 'api/actions/get-ev-charging-station-live-status/get-ev-charging-station-live-status-response';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import panic from 'panic';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import theme from 'theme';
import StationHeaderCard from 'pages/ev-charging-station/components/StationHeaderCard';
import UsageOverviewCard from 'pages/ev-charging-station/components/UsageOverviewCard';
import MonthlyUsageChartCard from 'pages/ev-charging-station/components/MonthlyUsageChartCard';
import ChargingHistoryCard from 'pages/ev-charging-station/components/ChargingHistoryCard';

type EvChargingStation = GetEvChargingStationResponse;
type EvChargingSessions = GetEvChargingSessionsResponse;
type EvChargingSessionStats = GetEvChargingSessionStatsResponse;

/** How often the live AgeVolt status is refreshed; the API caches it for the same time. */
const LIVE_STATUS_REFRESH_MS = 30_000;

/** Shown until the first live status arrives or when it cannot be loaded. */
const UNKNOWN_LIVE_STATUS: GetEvChargingStationLiveStatusResponse = {
  status: 'unknown',
  connectorCount: null,
  availableConnectorCount: null,
  powerType: null,
  maxPowerKw: null,
  plugType: null,
  activeSession: null,
  updatedAt: null,
};

/**
 * Detail page for one EV charging station: station info, the current
 * customer's usage overview, a monthly consumption chart, and their
 * charging history at this station.
 */
export function EvChargingStationDetailImpl({ evChargingStationId }: { evChargingStationId: number }) {
  const { _t } = useLang();
  const { getAction } = useApi();
  const [station, setStation] = useState<EvChargingStation | null>(null);
  const [sessions, setSessions] = useState<EvChargingSessions | null>(null);
  const [stats, setStats] = useState<EvChargingSessionStats | null>(null);
  const [liveStatus, setLiveStatus] = useState<GetEvChargingStationLiveStatusResponse>(UNKNOWN_LIVE_STATUS);

  useEffect(() => {
    if (!evChargingStationId) {
      return;
    }

    const getEvChargingStation = getAction('GetEvChargingStation');
    const getEvChargingSessions = getAction('GetEvChargingSessions');
    const getEvChargingSessionStats = getAction('GetEvChargingSessionStats');

    getEvChargingStation({ parameters: { evChargingStationId } }).then(setStation).catch(panic);
    getEvChargingSessions({ parameters: { evChargingStationId }, query: {} }).then(setSessions).catch(panic);
    getEvChargingSessionStats({ parameters: { evChargingStationId } }).then(setStats).catch(panic);
  }, [evChargingStationId]);

  useEffect(() => {
    if (!evChargingStationId) {
      return;
    }

    const getEvChargingStationLiveStatus = getAction('GetEvChargingStationLiveStatus');
    let cancelled = false;

    /**
     * Reloads the live status. It is optional: a failed refresh keeps the last
     * known state instead of breaking the page.
     */
    const refresh = () =>
      getEvChargingStationLiveStatus({ parameters: { evChargingStationId } })
        .then((value) => !cancelled && setLiveStatus(value))
        .catch(() => {});

    setLiveStatus(UNKNOWN_LIVE_STATUS);
    refresh();
    const interval = setInterval(refresh, LIVE_STATUS_REFRESH_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [evChargingStationId]);

  useEffect(() => {
    if (station?.name) {
      document.title = station.name;
    }
  }, [station?.name]);

  if (!station || !sessions || !stats) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="1rem">
          <Loader size="lg" color={theme.colors?.primary?.[0]} />
          <H2SemiBold>{_t('Loading charging station')}</H2SemiBold>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack bg="gray.0" gap={20} pb={40} mih="100vh">
      <StationHeaderCard station={station} liveStatus={liveStatus} />
      <Stack gap={20} px={16}>
        <UsageOverviewCard stats={stats} />
        <MonthlyUsageChartCard monthlyBreakdown={stats.monthlyBreakdown} />
        <ChargingHistoryCard sessions={sessions.sessions} />
      </Stack>
    </Stack>
  );
}

/**
 * Detail page for one EV charging station, resolved from the `:evChargingStationId` route param.
 */
export default function EvChargingStationDetail() {
  const { evChargingStationId: evChargingStationIdRaw = '' } = useParams();
  const evChargingStationId = useMemo(() => parseInt(evChargingStationIdRaw), [evChargingStationIdRaw]);

  return <EvChargingStationDetailImpl evChargingStationId={evChargingStationId} />;
}
